// Product Sales API — client layer for the standalone register + job-attached
// sales. Endpoints per tasks/server-requirements-checkout-product-sales.md §2.
// If the server shipped different shapes, adjust here (single source of truth
// for the register's server contract).

import { HTTPService } from './httpService';

const API = new HTTPService();

/** POST /api/product-sales — create a sale (draft or completed). */
export const createSale = async (saleData) => {
  const { data } = await API.post('/api/product-sales', saleData);
  if (data && data.error) {
    const err = new Error(data.message || 'Could not create sale.');
    err.stripeNotReady = !!data.stripeNotReady;
    err.reason = data.reason;
    throw err;
  }
  return data;
};

/** GET /api/product-sales/:id — sale with items + payment records. */
export const getSale = async (saleId) => {
  const { data } = await API.get(`/api/product-sales/${saleId}`);
  return data;
};

/** PUT /api/product-sales/:id — update a draft. */
export const updateSale = async (saleId, saleData) => {
  const { data } = await API.put(`/api/product-sales/${saleId}`, saleData);
  return data;
};

/**
 * POST /api/product-sales/:id/complete — atomic finalize: server deducts
 * inventory, creates the line-itemized invoice (card/online), and records the
 * payment. `paymentData` carries the tender method (+ checkNumber) chosen on the
 * register tiles. Returns the completed sale incl. invoice_url / stripe ids.
 */
export const completeSale = async (saleId, paymentData) => {
  const { data } = await API.post(`/api/product-sales/${saleId}/complete`, paymentData);
  if (data && data.success === false) {
    const err = new Error(data.message || data.error || 'Could not complete sale.');
    err.stripeNotReady = !!data.stripeNotReady;
    err.reason = data.reason;
    throw err;
  }
  return data;
};

/** POST /api/product-sales/:id/void — void a draft. */
export const voidSale = async (saleId) => {
  const { data } = await API.post(`/api/product-sales/${saleId}/void`, {});
  return data;
};

/** GET /api/product-sales — list (installerId, locationId, status, dateFrom, dateTo). */
export const getSales = async (installerId, params = {}) => {
  const { data } = await API.get('/api/product-sales', { installerId, ...params });
  return data;
};

/** GET /api/product-sales/summary — reporting aggregates. */
export const getSalesSummary = async (installerId, params = {}) => {
  const { data } = await API.get('/api/product-sales/summary', { installerId, ...params });
  return data;
};

/** GET /api/product-sales/upsold — job-attached (upsold) product lines list. */
export const getUpsold = async (installerId, params = {}) => {
  const { data } = await API.get('/api/product-sales/upsold', { installerId, ...params });
  return data;
};

const ProductSalesAPI = {
  createSale,
  getSale,
  updateSale,
  completeSale,
  voidSale,
  getSales,
  getSalesSummary,
  getUpsold,
};

export default ProductSalesAPI;
